import {
  Autocomplete,
  Box,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import { useId, useState } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';

import { Alert, CopyToClipboardButton, FormField, Loader, api, isDefined } from 'common';

import { TestRunEntitySourceVariant } from './config';
import { PracticeLabel } from '../../../../../../../components/Label/Practice';
import { formatSearchResultLink } from '../../../../../../../utils/navigation';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';

const entityTypeMap: Record<string, api.Search.Entity> = {
  Patient: api.Search.Entity.Patient,
  Encounter: api.Search.Entity.Encounter,
  Order: api.Search.Entity.Order,
  ExternalDocumentPacket: api.Search.Entity.ExternalDocumentPacket,
};

export function EntitySearch(props: {
  entityType: string;
  organizationId: string;
  entityId?: string;
  entity?: api.Search.Result;
  setEntity: (entity?: api.Search.Result) => void;
  disabled?: boolean;
  testRunEntitySourceVariant?: TestRunEntitySourceVariant;
}) {
  const {
    entityType,
    organizationId,
    entityId,
    entity,
    setEntity,
    disabled,
    testRunEntitySourceVariant,
  } = props;
  const { setValue: setEntityId } = useSelectedNodeField<string | undefined>('config.testEntityId');
  const { setValue: setPracticeId } = useSelectedNodeField<string | undefined>(
    'config.testPracticeId'
  );
  const [query, setQuery] = useState('');
  // this is here to ensure the initial query is only enabled once and is not performed again when a different entity is selected
  const [firstQueryEnabled, setFirstQueryEnabled] = useState(true);
  const entitySearchId = useId();
  const entityTypeEnum = entityTypeMap[entityType];
  const includeProductionEntities =
    testRunEntitySourceVariant === TestRunEntitySourceVariant.Production;
  const searchMode = includeProductionEntities
    ? api.Search.SearchMode.AllData
    : api.Search.SearchMode.TestOnly;
  const { data: searchResults, isFetching: searchFetching } = useQuery({
    queryKey: [
      api.client.Search.baseEndpoint,
      'flowBuilderSearch',
      query,
      entityType,
      organizationId,
      searchMode,
    ],
    queryFn: async () => {
      try {
        if (!query || !entityType || entityType === 'None') {
          return [];
        }
        return await api.client.Search.flowBuilderSearch(
          query,
          entityTypeEnum,
          organizationId,
          searchMode
        );
      } catch (err) {
        console.error('Error searching results:', err);
        return null;
      }
    },
    enabled: !!query && !!entityType,
  });

  const { isFetching: initialFetching, error: initialError } = useQuery({
    queryKey: [
      api.client.Search.baseEndpoint,
      'flowBuilderSearch',
      entityId,
      entityTypeEnum,
      organizationId,
      searchMode,
    ],
    queryFn: async () => {
      setFirstQueryEnabled(false);
      if (!entityId) {
        return [];
      }
      const results = await api.client.Search.flowBuilderSearch(
        entityId,
        entityTypeEnum,
        organizationId,
        searchMode
      );
      // FIXME - this is a hack to set the initial value of the entity but useQuery ideally should not have side effects
      setEntity(results[0]);
      return results;
    },
    enabled: !isDefined(entity) && firstQueryEnabled,
  });

  if (initialFetching) {
    return <Loader label={`Loading ${entityType}...`} />;
  }

  if (initialError) {
    return <Alert title={`Error loading selected patient ${entityId}`} error={initialError} />;
  }

  let placeholder = 'Search';
  switch (entityType) {
    case 'Patient':
      placeholder = 'Enter MRN';
      if (!includeProductionEntities) {
        placeholder += ` or patient's last name`;
      }
      break;
    case 'ExternalDocumentPacket':
      placeholder = 'Enter UUID';
      break;
  }

  return (
    <FormField label={`${_.startCase(entityType)} Search`} id={entitySearchId}>
      <Stack gap={2}>
        <Autocomplete
          id={entitySearchId}
          filterOptions={(options) => options}
          loading={searchFetching}
          getOptionLabel={(option) => option.entity.name}
          options={searchResults || []}
          disabled={disabled}
          onInputChange={(event, input) => {
            // don't trigger the lookup query for click events or clearing the input
            if (event?.type === 'change') {
              setQuery(input);
            }
          }}
          onChange={async (_event, value) => {
            if (isDefined(value?.practiceId)) {
              setPracticeId(value.practiceId);
            }
            setEntityId(value?.id);
            setEntity(value ?? undefined);
          }}
          value={entity}
          loadingText="Searching..."
          renderInput={(params) => (
            <TextField {...params} placeholder={placeholder} variant="outlined" />
          )}
          renderOption={(menuItemProps, option) => (
            <MenuItem {...menuItemProps} key={option.id}>
              <Box display="flex" justifyContent="space-between" width="100%">
                <span>{option.entity.name}</span>
                <span>{option.organization}</span>
              </Box>
            </MenuItem>
          )}
        />
        {isDefined(entity) && (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>
                    <b>Patient</b>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Link
                        component={ReactRouterLink}
                        to={formatSearchResultLink(entity)}
                        target="_blank"
                      >
                        {entity.entity.name}
                      </Link>
                      <CopyToClipboardButton animationVariant="AlwaysVisible" content={entity.id} />
                    </Stack>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <b>Organization</b>
                  </TableCell>
                  <TableCell>{entity.organization}</TableCell>
                </TableRow>
                {isDefined(entity.practiceId) && (
                  <TableRow>
                    <TableCell>
                      <b>Practice</b>
                    </TableCell>
                    <TableCell>
                      <PracticeLabel practiceId={entity.practiceId} />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    </FormField>
  );
}
